import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Logo from "@/components/feature/Logo";
import { useAdminAuth, type LoginError } from "@/hooks/useAdminAuth";
import { useAdminI18n, type AdminLang } from "@/hooks/useAdminI18n";

const langOptions: { value: AdminLang; label: string; flag: string }[] = [
  { value: "de", label: "Deutsch", flag: "🇩🇪" },
  { value: "tr", label: "Türkçe", flag: "🇹🇷" },
];

function getErrorMessage(error: LoginError, detail?: string, t?: (key: string) => string): string {
  const tr = t || ((k: string) => k);
  switch (error) {
    case "invalid_credentials":
      return tr("login.error.invalid");
    case "no_profile":
      return tr("login.error.noProfile");
    case "not_admin":
      return `${tr("login.error.unauthorized")}${detail ? ` (role: ${detail})` : ""}`;
    case "supabase_error":
      return detail || tr("login.error.supabase");
    default:
      return detail || tr("login.error.unknown");
  }
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAdminAuth();
  const { lang, setLang, t } = useAdminI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg("");
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      navigate("/admin");
    } else {
      setErrorMsg(getErrorMessage(result.error, result.detail, t));
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hj-gray-light flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Language switcher */}
        <div className="flex justify-end mb-3 gap-1">
          {langOptions.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setLang(opt.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                lang === opt.value
                  ? "border-hj-red text-hj-red bg-hj-red/5"
                  : "border-hj-gray-border text-hj-gray hover:bg-white"
              }`}
            >
              <span>{opt.flag}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg border border-hj-gray-border p-8">
          <div className="text-center mb-8">
            <div className="inline-flex mb-4">
              <Logo variant="header" />
            </div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("login.title")}</h1>
            <p className="text-sm text-hj-gray mt-1">{t("login.subtitle")}</p>
          </div>

          {errorMsg && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
              <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                <i className="ri-error-warning-line text-red-500 text-sm" />
              </div>
              <p className="text-sm text-red-600">{errorMsg}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-hj-dark mb-1.5">
                {t("login.email")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                placeholder="admin@example.com"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-hj-dark mb-1.5">
                {t("login.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-hj-gray hover:text-hj-dark cursor-pointer"
                  tabIndex={-1}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={showPassword ? "ri-eye-off-line text-sm" : "ri-eye-line text-sm"} />
                  </div>
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-hj-red text-white font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  {t("login.submitting")}
                </span>
              ) : (
                t("login.submit")
              )}
            </button>

            {/* Forgot password link */}
            <div className="text-center">
              <Link
                to="/admin/passwort-vergessen"
                className="text-sm text-hj-gray hover:text-hj-red transition-colors"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>
          </form>

          {/* Help text */}
          <div className="mt-5 p-3 bg-hj-gray-light rounded-md">
            <p className="text-xs text-hj-gray leading-relaxed">
              {t("login.helpText")}
            </p>
          </div>

          <div className="mt-6 pt-4 border-t border-hj-gray-border text-center">
            <p className="text-[11px] text-gray-400">
              {t("footer.developedBy")}{" "}
              <a
                href="https://kreativweb.ch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-hj-red transition-colors"
              >
                KreativWeb.ch
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
